import { apiUrl } from "../../utils/api";
import type { DocumentDto } from "../../services/documentService";

/**
 * Unterlagen über den öffentlichen Kundenlink einer Immobilie
 * (`public-form/:token/uploads`, ohne Anmeldung). Liefert dieselbe Form wie
 * `documentService`, damit der Datei-Puffer beide Wege gleich behandelt.
 *
 * `eigene: false` kennzeichnet Dateien, die der Berater hochgeladen hat — die
 * darf der Kunde sehen, aber nicht löschen.
 */

export type KundenlinkDokument = DocumentDto & { eigene: boolean };

type RohDatei = {
  documentId: number;
  fileName: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  uploadedAt?: string | null;
  eigene?: boolean;
};

const alsDokument = (slot: string, d: RohDatei): KundenlinkDokument => ({
  id: d.documentId,
  fileName: d.fileName,
  mimeType: d.mimeType ?? null,
  fileSize: d.sizeBytes ?? null,
  uploadedAt: d.uploadedAt ?? null,
  tags: [`immo-slot:${slot}`],
  eigene: d.eigene !== false,
});

/** Antwort von `GET public-form/:token/uploads` → flache Dokumentliste. */
export const dokumenteAusAntwort = (antwort: unknown): KundenlinkDokument[] => {
  const dateien = (antwort as { dateien?: Record<string, RohDatei[]> } | null)?.dateien ?? {};
  return Object.entries(dateien).flatMap(([slot, liste]) =>
    (Array.isArray(liste) ? liste : []).map((d) => alsDokument(slot, d)),
  );
};

const fehlertext = async (res: Response, standard: string): Promise<string> => {
  try {
    const body = (await res.json()) as { message?: string | string[] };
    const m = Array.isArray(body?.message) ? body.message[0] : body?.message;
    return m || standard;
  } catch {
    return standard;
  }
};

export const kundenlinkUploads = (token: string) => {
  const basis = `public-form/${encodeURIComponent(token)}/uploads`;
  return {
    async list(): Promise<KundenlinkDokument[]> {
      const res = await fetch(apiUrl(basis));
      if (!res.ok) throw new Error(await fehlertext(res, "Dateien konnten nicht geladen werden."));
      return dokumenteAusAntwort(await res.json());
    },
    async upload(file: File, slot: string): Promise<KundenlinkDokument> {
      const form = new FormData();
      // `slot` VOR der Datei — der Server liest die Felder in Reihenfolge.
      form.append("slot", slot);
      form.append("file", file);
      const res = await fetch(apiUrl(basis), { method: "POST", body: form });
      if (!res.ok) throw new Error(await fehlertext(res, "Upload fehlgeschlagen."));
      const d = (await res.json()) as RohDatei & { slot?: string };
      return alsDokument(d.slot ?? slot, d);
    },
    async remove(documentId: number): Promise<void> {
      const res = await fetch(apiUrl(`${basis}/${documentId}`), { method: "DELETE" });
      if (!res.ok) throw new Error(await fehlertext(res, "Die Datei konnte nicht gelöscht werden."));
    },
  };
};
